import ReactEChartsCore from 'echarts-for-react/lib/core';
import { ScatterChart } from 'echarts/charts';
import {
  GridComponent,
  LegendComponent,
  TooltipComponent
} from 'echarts/components';
import * as echarts from 'echarts/core';
import type { EChartsOption } from 'echarts';
import {
  breakpointsMediaQuery,
  getColor,
  getRandomColor,
  useMediaQuery
} from '@/helpers/utils';
import { Card, Col, Row } from 'react-bootstrap';
import { useRef } from 'react';
import { serpTooltips } from '@/app/Serp/tooltip/serpTooltips';
import FalconCardHeader from '@/components/common/FalconCardHeader';
import HelpTooltip from '@/app/common/components/HelpTooltip';
import CardChartDropDown from '@/components/common/CardChartDropDown';
import CustomLegent from '@/app/common/components/charts/CustomLegent';
import { useTooltipHide } from '@/hooks/useTooltipHide';
import type { SerpAnalysisDomains } from '@/app/Serp/types/serpApiTypes';
import timeStampToDate from '@/helpers/timeStampToDate';
echarts.use([ScatterChart, TooltipComponent, GridComponent, LegendComponent]);

const formatMonthYear = (value: unknown) => {
  const rawValue = String(value ?? '');
  const directDate = rawValue.match(/^(\d{4})[-/](\d{1,2})/);
  if (directDate) {
    return `${directDate[1]}/${directDate[2]}`.toPersianDigit();
  }

  const date = timeStampToDate(Number(value)).split(' ')[1];
  return date
    ? date.split('-').slice(0, 2).join('/').toPersianDigit()
    : rawValue.toPersianDigit();
};

interface DomainAuthorityHistoryProps {
  data?: SerpAnalysisDomains['domains_moz'];
  embedded?: boolean;
  legendBottom?: boolean;
}

const DomainAuthorityHistory = ({
  data,
  embedded = false,
  legendBottom = false
}: DomainAuthorityHistoryProps) => {
  const chartRef = useRef<ReactEChartsCore | null>(null);
  const isMdUp = useMediaQuery(breakpointsMediaQuery.md);
  useTooltipHide(chartRef);

  const cardRef = useRef<HTMLDivElement | null>(null);
  const categories = data?.moz_categories;
  const formattedSeries = data?.moz_data?.map(item => {
    const c = getRandomColor();
    return {
      type: 'line',
      smooth: false,
      name: item.name,
      data: item.data,
      symbol: 'emptyCircle',
      itemStyle: {
        color: getColor(c)
      },
      lineStyle: {
        color: getColor(c)
      },

      emphasis: {
        lineStyle: {
          width: 2
        }
      }
    };
  });

  // تنظیمات نمودار
  const getOption = (): EChartsOption => ({
    grid: { right: '5%', left: '7%', bottom: 60, top: '5%' },
    textStyle: {
      fontFamily: 'dana'
    },
    legend: {
      show: false
    },
    tooltip: {
      triggerOn: isMdUp ? 'mousemove' : 'click',

      trigger: 'axis',
      padding: [7, 10],
      backgroundColor: getColor('gray-100'),
      borderColor: getColor('gray-300'),
      textStyle: { color: getColor('gray-1100') },
      borderWidth: 1,
      formatter: (params: any) => {
        const formattedParams = params
          ?.map(({ seriesName, value, marker }: any) => {
            return `<span class="d-flex justify-content-between" style="direction:ltr;margin:0;padding:0"><span class="fw-bolder">${marker} ${isMdUp ? seriesName.toPersianDigit() : ''}:</span> ${value.toString().toPersianDigit()}</span>`;
          })
          ?.join('');
        return `
        <div style="font-family: dana !important;">
        <div class="mb-2 d-flex gap-1">
        <span>رتبه در </span><span class="fw-bold">${formatMonthYear(params[0]?.axisValue)}</span>
        </div>  
        <div style="direction:rtl;" class="d-flex flex-column">
          ${formattedParams}  
          </div>
        </div>`;
      },
      transitionDuration: 0,
      axisPointer: {
        // type: 'shadow'
      }
    },
    xAxis: {
      data: categories,
      axisLine: {
        lineStyle: {
          color: getColor('gray-300'),
          type: 'solid'
        }
      },
      axisLabel: {
        show: true,
        color: getColor('gray-800'),
        interval: false,
        rotate: 45
        // margin: 5
      },
      splitLine: {
        show: true,
        lineStyle: {
          color: getColor('gray-200')
        }
      }
    },
    yAxis: {
      name: 'رتبه',
      nameLocation: 'center',
      nameTextStyle: { color: getColor('gray-900'), padding: 5 },
      axisLine: {
        lineStyle: {
          color: getColor('gray-300'),
          type: 'solid'
        }
      },
      axisLabel: {
        show: true,
        color: getColor('gray-800')
        // margin: 5
      },
      splitLine: {
        show: true,
        lineStyle: {
          color: getColor('gray-200')
        }
      }
    },
    series: formattedSeries
  }) as unknown as EChartsOption;

  const chartBody = embedded || legendBottom ? (
    <div>
      <ReactEChartsCore
        ref={chartRef}
        echarts={echarts}
        option={getOption()}
        style={{ height: '23rem' }}
      />
      <div className="d-flex flex-wrap justify-content-center gap-2 mt-2">
        {formattedSeries?.map(item => (
          <CustomLegent
            chartRef={chartRef}
            name={item.name}
            color={item.itemStyle.color}
            key={item.name}
          />
        ))}
      </div>
    </div>
  ) : (
    <Row>
      <Col xxl={2} md={4} className="overflow-hidden mt-3">
        {formattedSeries?.map(item => (
          <CustomLegent
            chartRef={chartRef}
            name={item.name}
            color={item.itemStyle.color}
            key={item.name}
          />
        ))}
      </Col>
      <Col xxl={10} md={8}>
        <ReactEChartsCore
          ref={chartRef}
          echarts={echarts}
          option={getOption()}
          style={{ height: '23rem' }}
        />
      </Col>
    </Row>
  );

  if (embedded) return chartBody;

  return (
    <Card className="mb-3" ref={cardRef}>
      <FalconCardHeader
        title={
          <div>
            تاریخچه دامین آتوریتی
            <HelpTooltip html={serpTooltips.domainAuthority} />{' '}
          </div>
        }
        titleTag="h6"
        className="py-2"
        light
        endEl={<CardChartDropDown cardRef={cardRef} chartRef={chartRef} />}
      />
      <Card.Body className="px-md-6">{chartBody}</Card.Body>
    </Card>
  );
};
export default DomainAuthorityHistory;
